import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import { LLMConfig } from '../../types/admin';
import { LLMConfigForm } from '../../components/admin/LLMConfigForm';
import { RefreshCw } from 'lucide-react';

export const LLMConfigTab: React.FC = () => {
  const [activeConfigTab, setActiveConfigTab] = useState<'llm' | 'vision' | 'director'>('llm');
  const [llmConfig, setLlmConfig] = useState<LLMConfig | null>(null);
  const [visionConfig, setVisionConfig] = useState<LLMConfig | null>(null);
  const [directorConfig, setDirectorConfig] = useState<LLMConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const [llmRes, visionRes, directorRes] = await Promise.all([
        apiClient.get('/api/admin/llm-config'),
        apiClient.get('/api/admin/vision-llm-config'),
        apiClient.get('/api/admin/director-llm-config'),
      ]);
      setLlmConfig(llmRes.data);
      setVisionConfig(visionRes.data);
      setDirectorConfig(directorRes.data);
    } catch (error) {
      console.error('Failed to fetch LLM configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLLM = async (config: LLMConfig) => {
    await apiClient.put('/api/admin/llm-config', config);
    await fetchConfigs();
  };

  const handleTestLLM = async (config: LLMConfig) => {
    await apiClient.post('/api/admin/llm-config/test', config);
  };

  const handleSaveVision = async (config: LLMConfig) => {
    await apiClient.put('/api/admin/vision-llm-config', config);
    await fetchConfigs();
  };

  const handleTestVision = async (config: LLMConfig) => {
    // Vision LLM 没有测试端点，使用相同的测试逻辑
    await apiClient.post('/api/admin/llm-config/test', config);
  };

  const handleSaveDirector = async (config: LLMConfig) => {
    await apiClient.put('/api/admin/director-llm-config', config);
    await fetchConfigs();
  };

  const handleTestDirector = async (config: LLMConfig) => {
    // Director LLM 没有测试端点，使用相同的测试逻辑
    await apiClient.post('/api/admin/llm-config/test', config);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 配置类型标签 */}
      <div className="border-b border-cyan-900/30">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveConfigTab('llm')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeConfigTab === 'llm'
                ? 'border-cyan-400 text-cyan-400'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-cyan-900/30'
            }`}
          >
            提示词优化 LLM
          </button>
          <button
            onClick={() => setActiveConfigTab('vision')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeConfigTab === 'vision'
                ? 'border-cyan-400 text-cyan-400'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-cyan-900/30'
            }`}
          >
            Vision LLM
          </button>
          <button
            onClick={() => setActiveConfigTab('director')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeConfigTab === 'director'
                ? 'border-cyan-400 text-cyan-400'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-cyan-900/30'
            }`}
          >
            Director LLM
          </button>
        </nav>
      </div>

      {/* 配置表单 */}
      <div>
        {activeConfigTab === 'llm' && llmConfig && (
          <LLMConfigForm
            title="提示词优化 LLM 配置"
            description="用于优化图片生成提示词的大语言模型配置"
            initialConfig={llmConfig}
            onSave={handleSaveLLM}
            onTest={handleTestLLM}
          />
        )}

        {activeConfigTab === 'vision' && visionConfig && (
          <LLMConfigForm
            title="Vision LLM 配置"
            description="用于图片理解和生成提示词的视觉大语言模型配置"
            initialConfig={visionConfig}
            onSave={handleSaveVision}
            onTest={handleTestVision}
          />
        )}

        {activeConfigTab === 'director' && directorConfig && (
          <LLMConfigForm
            title="Director LLM 配置"
            description="用于 AI 导演工作台的大语言模型配置"
            initialConfig={directorConfig}
            onSave={handleSaveDirector}
            onTest={handleTestDirector}
          />
        )}
      </div>
    </div>
  );
};
